'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type Transition, motion } from 'framer-motion';
import { Bluetooth, CircleSlash, Cpu, Monitor, MonitorUp, Usb } from 'lucide-react';
import { queryChipIdOverBridge } from '@/hooks/feature/boards/useBoardBridgeChipIdQuery';
import { useBoardBroadcast } from '@/hooks/feature/boards/useBoardBroadcast';
import { useBoardUpload } from '@/hooks/feature/boards/useBoardUpload';
import { useBoardWebSerialApi } from '@/hooks/feature/boards/useBoardWebSerialApi';
import { useBoardWebSocketLocal } from '@/hooks/feature/boards/useBoardWebSocketLocal';
import { useHandleError } from '@/hooks/useHandleError';
import { apiFetch, apiFetchWithQuery } from '@/lib/api/apiClient';
import { useBoardEditorConfigStore } from '@/stores/useBoardEditorConfigStore';
import { useBoardEditorFileStore } from '@/stores/useBoardEditorFileStore';
import { useBoardEditorStore } from '@/stores/useBoardEditorStore';
import type { BoardIdentifyResponse } from '@/stores/useBoardEditorStore';
import { useModalStore } from '@/stores/useModalStore';
import { useToastStore } from '@/stores/useToastStore';
import ButtonGAdaptive from '@/components/ButtonGAdaptive';
import IconAdaptive from '@/components/IconAdaptive';
import ImageAdaptive from '@/components/ImageAdaptive';
import { REQUIRED_BRIDGE_VERSION } from '@/constants/constants';
import { isVersionOlder } from '@/utils/helpers/bridgeVersionHelper';
import { BOARD_CONFIG, BoardType, getInitialCode } from '@/(routers)/boards/editor/block/blockConfig';
import { HUB_AFFILIATION, hubTypeCdToAffiliation, isUsableHubType } from '@/(routers)/boards/editor/block/boardIdentity';
import { BOARD } from '@/(routers)/boards/editor/block/definitions/boards';
import BoardSelectorAuto from '@/(routers)/boards/editor/components/modal/BoardSelectorAuto';
import BoardSelectorAutoWebSerialApi from '@/(routers)/boards/editor/components/modal/BoardSelectorAutoWebSerialApi';

const BOARD_UI = {
    // メニューと同じ色(#FF8000/#2F5AA4)を薄めた版。Tailwindの/opacity修飾子は
    // このプロジェクトのカスタムカラーで生成されないため、rgbaを直書きしている。
    orange: { label: BOARD_CONFIG.orange.label, bgColor: 'rgba(255, 128, 0, 0.3)' },
    blue: { label: BOARD_CONFIG.blue.label, bgColor: 'rgba(47, 90, 164, 0.3)' },
} as const;

// 検出中/未接続時のアイコン背景。ボードは選択済みだがまだ実際に繋がっていないことを示す。
const DISCONNECTED_BG_COLOR = 'rgba(156, 163, 175, 0.3)';

const UPLOAD_LABEL_MIN_WIDTH = 1024;

export default function BoardToolbar() {
    const {
        board,
        port,
        code,
        hasSelectedBoard,
        isWebSocketConnected,
        isBridgeConnected,
        isBridgeStatusResolved,
        detectedBoards,
        monitorXMode,
        monitorTimeWindow,
        monitorYMode,
        monitorYMin,
        monitorYMax,
        isUploading,
        activeUploadRequestId,
        uploadStatus,
        uploadProgress,
        uploadMessage,
        osType,
        isMonitorOpen,
        boardIdentity,
        chipIdQueryStatus,
        bridgeVersion,
        getBaud,
        setMonitorXMode,
        setMonitorTimeWindow,
        setMonitorYMode,
        setMonitorYMin,
        setMonitorYMax,
        setBoard,
        setPort,
        setCode,
        setHasSelectedBoard,
        setWebSocketConnected,
        setIsBridgeConnected,
        setIsBridgeStatusResolved,
        setDetectedBoards,
        appendLog,
        appendErrorLog,
        setIsUploading,
        resetUpload,
        applyUploadEvent,
        setActiveUploadRequestId,
        setIsMonitorOpen,
        appendMonitorMessage,
        appendMonitorMessageBatch,
        setChipId,
        setBoardIdentity,
        setChipIdQueryStatus,
        setBridgeVersion,
        setLatestBridgeVersion,
        setTriggerBridgeUpdateCheck,
        setIsCheckingBridgeUpdate,
        setBridgeUpdateCheckMessage,
    } = useBoardEditorStore();

    const { isDark } = useBoardEditorConfigStore();

    const baud = getBaud();
    const { currentFileId, files } = useBoardEditorFileStore();
    const currentBlockXml = files.find((f) => f.id === currentFileId)?.blockContent ?? null;
    const [isFontReady, setIsFontReady] = useState(false);
    // SSR時とクライアント初回描画を一致させるためのガード。
    // ブリッジ判定状態(isBridgeStatusResolved等)はクライアントのuseEffectでしか変化しないはずだが、
    // ストアがモジュールシングルトンのため、SPA内の再訪問等でサーバー側の初期値とズレてhydration
    // エラーになるケースがあるため、mounted完了までは常にfalse相当で描画する。
    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);
    const { openModal } = useModalStore();
    const { handleError } = useHandleError();
    const showToastAsync = useToastStore((s) => s.showToastAsync);
    const isChromebook = osType === 'chromebook';
    // ブリッジ経由 or ブラウザ(Web Serial)経由、どちらの書き込み/検出チャネルを使うか。
    // Chromebookは常時true。それ以外はブリッジ未接続ならボード種別を問わずフォールバック(対称設計)。
    const useWebSerialPath = isChromebook || !isBridgeConnected;
    const [isUploadLabelVisible, setIsUploadLabelVisible] = useState(false);
    const monitorWindowRef = useRef<Window | null>(null);
    // ブリッジがchip_id読み取り/アップロードのためにモニターを一時停止/再開した際、モニター窓へ
    // 転送するための関数。useBoardBroadcast(下記)が返す関数を保持するだけのref(hooksの呼び出し
    // 順序上、useBoardWebSocketLocalより後でしかuseBoardBroadcastを呼べないため、refで仲介する)。
    const sendSerialMonitorStatusRef = useRef<((port: string, status: string) => void) | null>(null);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const mq = window.matchMedia(`(min-width: ${UPLOAD_LABEL_MIN_WIDTH}px)`);
        const update = () => setIsUploadLabelVisible(mq.matches);
        update();
        mq.addEventListener('change', update);
        return () => mq.removeEventListener('change', update);
    }, []);

    useEffect(() => {
        if (typeof document !== 'undefined' && (document as any).fonts?.ready) {
            (document as any).fonts.ready.then(() => setIsFontReady(true));
        } else {
            const t = setTimeout(() => setIsFontReady(true), 300);
            return () => clearTimeout(t);
        }
    }, []);

    // 物理的な接続状態(検出済みボードとして一致しているか)。chip_id問い合わせのトリガーにはこちらを使う
    // (下のisBoardConnectedはhub所属の判定結果に依存するため、そちらをトリガーに使うと問い合わせが
    // 永遠に発火しないデッドロックになる)。
    // ペアリング済みのBluetooth仮想COMポートはUSBを抜いた後もOS上に残り続けるため、
    // detectedBoardsに`is_bluetooth: true`のエントリとして残ることがある。親ツールバーの
    // 「USB接続中」判定にこれを含めると、USBを物理的に抜いてもBluetoothの残骸とマッチして
    // 接続中のまま固まってしまう(モーダル側のusbBoardsフィルタと同じ考え方に揃える)。
    const isPhysicallyConnected = useMemo(() => {
        if (!hasSelectedBoard) return false;
        const isBoardDetected = detectedBoards.some((b) => !b.is_bluetooth && b.type === board);
        if (useWebSerialPath) return isBoardDetected;
        return isBridgeConnected && isWebSocketConnected && isBoardDetected;
    }, [hasSelectedBoard, board, detectedBoards, useWebSerialPath, isBridgeConnected, isWebSocketConnected]);

    // 今selectedBoard種別として検出されている実機のUSBシリアル番号。ポート/デバイスパスは
    // 環境によっては別の実機に差し替えても変わらないことがあるため(特にMac。Windowsは
    // シリアル番号ごとに別COMポート番号を振る傾向がある)、ポートの有無だけを見る
    // isPhysicallyConnectedのtrue→false遷移では「同じポートのまま別の実機に入れ替わった」
    // ケースを検出できない。serial_numberの変化を見て、その場合も切断相当として扱う。
    const matchedBoardSerialNumber = useMemo(
        () => detectedBoards.find((b) => !b.is_bluetooth && b.type === board)?.serial_number ?? null,
        [detectedBoards, board],
    );

    // 確定して未所属/未対応hub種別だったブルーボードのみ使用不可(モニター/書き込みとも不可)。
    const isConfirmedUnusableBlueBoard =
        board === 'blue' && boardIdentity !== null && !isUsableHubType(boardIdentity.hub_type_cd);
    // Web Serial APIはブリッジ非依存で常にROM経由のchip_id取得に対応している。ブリッジ経由は
    // `read_chip_id`メッセージ(REQUIRED_BRIDGE_VERSION以降)に対応した版のみ。旧ブリッジは
    // このメッセージを認識せず無応答のため、chipIdQueryStatusが永久にtimed_outで固まる。
    // bridgeVersionが未解決(旧ブリッジは`request_bridge_version`にも応答しないため常にnullの
    // まま)の場合も含め、対応が確認できない間はゲート自体を無効化し、ブルーもオレンジ同様
    // isPhysicallyConnectedのみで使用可否を判定する(後方互換: 旧ブリッジ利用者の書き込み/
    // モニターを壊さないため。新機能を使えないだけで、これまで通りの挙動に留める)。
    const bridgeSupportsChipId =
        useWebSerialPath || (!!bridgeVersion && !isVersionOlder(bridgeVersion, REQUIRED_BRIDGE_VERSION));
    // chip_id自体が不明(unknown)なブルーボードも使用不可にする。ブリッジ・Web Serial API
    // どちらもROM経由(ファームウェア不問)でchip_idを取得するため、書き込みにchip_id解決が
    // 不要で「書き込みで初めて判明する」循環が起きずデッドロックしない(以前はブリッジのみ
    // 旧SCMY_ID?方式でこの循環が起きるため対象外にしていたが、ROM方式へ統一済みのため撤廃)。
    const isUnknownBlueBoard = board === 'blue' && bridgeSupportsChipId && chipIdQueryStatus !== 'succeeded';
    const isBoardConnected = isPhysicallyConnected && !isConfirmedUnusableBlueBoard && !isUnknownBlueBoard;

    useEffect(() => {
        if (!isUploading) return;

        if (uploadStatus === 'success' && uploadProgress >= 100) {
            const t = window.setTimeout(() => {
                setIsUploading(false);
                resetUpload();
                setActiveUploadRequestId(null);
            }, 2000);
            const toast = window.setTimeout(() => {
                showToastAsync('スクーミーボードに書き込みが完了しました！', 'success', 5000);
            }, 500);

            // ホワイト先生の表示ON/OFFに関係なく、書き込み成功は必ずログに記録する
            apiFetch('/boards/editor/flash', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ board_type: board, block_xml: currentBlockXml }),
            }).catch(() => {
                // ログ失敗はサイレントに無視
            });

            window.dispatchEvent(new CustomEvent('board-upload-success', { detail: { success: true } }));

            return () => {
                window.clearTimeout(t);
                window.clearTimeout(toast);
            };
        }

        if (uploadStatus === 'error') {
            const t = window.setTimeout(() => {
                setIsUploading(false);
                resetUpload();
                setActiveUploadRequestId(null);
            }, 3000);
            showToastAsync(uploadMessage || 'スクーミーボードへの書き込みに失敗しました。', 'error', 5000);
            appendLog('💡 一度失敗しても、再度書き込みを実行すると成功する場合があります。');
            appendLog('💡 何度も失敗する場合は、以下をお試しください。');
            appendLog('『USBを抜き差しして、再度書き込みを実施する』');
            appendLog(
                '『スクーミーボードのリセットボタンを押しながら書き込みを開始し、書き込みが始まったらボタンを離す』',
            );

            return () => window.clearTimeout(t);
        }
    }, [
        isUploading,
        uploadStatus,
        uploadProgress,
        uploadMessage,
        board,
        currentBlockXml,
        setIsUploading,
        resetUpload,
        setActiveUploadRequestId,
        showToastAsync,
    ]);

    const { wsRef, requestBoardList, checkBridgeUpdateNow } = useBoardWebSocketLocal({
        enabled: !isChromebook,
        onLog: appendLog,
        // 検出結果が空でない場合は useWebSerialPath の状態に関係なく常に反映する。
        // (ブリッジ接続直後の最初のメッセージは isBridgeConnected 反映前の古い useWebSerialPath を
        //  見てしまい、ガードで握りつぶされると二度と更新されなくなるレースを避けるため)
        // 空配列(クリア)の場合のみ、Web Serial側のアクティブなセッションを壊さないよう従来通りガードする。
        onBoardsDetected: (boards) => (boards.length > 0 || !useWebSerialPath) && setDetectedBoards(boards),
        onPortUpdate: (newPort) => (newPort !== null || !useWebSerialPath) && setPort(newPort),
        onSerialMonitorStatus: (port, status) => sendSerialMonitorStatusRef.current?.(port, status),
        onWebSocketConnected: setWebSocketConnected,
        onBridgeConnected: (connected: boolean) => {
            setIsBridgeConnected(connected);
            setIsBridgeStatusResolved(true);
        },
        hasSelectedBoard,
        selectedBoard: board,
        getActiveRequestId: () => activeUploadRequestId,
        onUploadEvent: (e) => applyUploadEvent(e),
        onBridgeVersion: setBridgeVersion,
        onLatestBridgeVersion: setLatestBridgeVersion,
        onBridgeUpdateCheckResult: (result) => {
            setIsCheckingBridgeUpdate(false);
            setBridgeUpdateCheckMessage(result.message);
            if (result.latest_version) setLatestBridgeVersion(result.latest_version);
        },
    });

    // 設定モーダル(BoardEditorSettings.tsx)は別コンポーネントでこのWebSocket接続を
    // 直接持っていないため、「今すぐ更新を確認」ボタンから呼べるようにストア経由で
    // トリガー関数を橋渡しする。アンマウント時はnullに戻す(古いwsRefを掴んだままにしない)。
    useEffect(() => {
        setTriggerBridgeUpdateCheck(() => {
            setIsCheckingBridgeUpdate(true);
            setBridgeUpdateCheckMessage(null);
            checkBridgeUpdateNow();
        });
        return () => setTriggerBridgeUpdateCheck(null);
    }, [checkBridgeUpdateNow, setTriggerBridgeUpdateCheck, setIsCheckingBridgeUpdate, setBridgeUpdateCheckMessage]);

    // 最新のboard/hasSelectedBoardをrefで参照する。これらをeffectの依存配列に含めると、
    // ブロックの色設定やSettingsからの手動setBoard()のたびにこのeffectが再評価され、
    // まだ物理検出されていない種別への手動切り替えを「差し替わった」と誤認して差し戻してしまう。
    // detectedBoards自体が変化した時(=物理的な抜き差し)だけ判定する。
    const boardRef = useRef(board);
    const hasSelectedBoardRef = useRef(hasSelectedBoard);
    useEffect(() => {
        boardRef.current = board;
        hasSelectedBoardRef.current = hasSelectedBoard;
    }, [board, hasSelectedBoard]);

    // 初回選択はモーダルでの手動選択が必須(自動選択はしない)。選択済みの場合のみ、選択中の
    // ボードが外れて別種のボードに差し替わった時に、モーダルを開き直さなくても自動で切り替える
    useEffect(() => {
        if (isChromebook) return;
        if (!isBridgeConnected || !isWebSocketConnected) return;
        if (!hasSelectedBoardRef.current) return;
        const usbBoards = detectedBoards.filter((b) => !b.is_bluetooth);
        if (usbBoards.length === 0) return;

        const stillConnected = usbBoards.some((b) => b.type === boardRef.current);
        if (!stillConnected) {
            const replacement = usbBoards[0];
            setBoard(replacement.type);
            setPort(replacement.port);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detectedBoards, isBridgeConnected, isWebSocketConnected, isChromebook]);

    // chip_idが解決できた時に一度だけ呼ぶ。DBの実データ(ボード名/BT名/hub所属)を取得してキャッシュする。
    const fetchBoardIdentity = useCallback(
        async (id: number) => {
            try {
                const response = await apiFetchWithQuery('/boards/identify', { chip_id: id });
                setBoardIdentity(response as BoardIdentifyResponse);
            } catch {
                setBoardIdentity(null);
            }
        },
        [setBoardIdentity],
    );

    const {
        connect,
        disconnect,
        writeToSerial,
        webSerialApiUpload,
        isChipIdProbing,
    } = useBoardWebSerialApi({
        hasSelectedBoard,
        selectedBoard: board,
        isMonitorOpen: isMonitorOpen,
        isUploading: false,
        appendLog: appendLog,
        appendErrorLog: appendErrorLog,
        onBoardsDetected: (boards) => useWebSerialPath && setDetectedBoards(boards),
        onPortUpdate: (newPort) => useWebSerialPath && setPort(newPort),
        getActiveRequestId: () => activeUploadRequestId,
        onUploadEvent: (e) => applyUploadEvent(e),
        appendMonitorMessage: appendMonitorMessage,
        appendMonitorMessageBatch: appendMonitorMessageBatch,
        setHasSelectedBoard,
        onChipIdResolved: (id) => {
            setChipId(id);
            if (id !== null) {
                setChipIdQueryStatus('succeeded');
                fetchBoardIdentity(id);
            } else {
                setChipIdQueryStatus('timed_out');
            }
        },
        onIdentifyMismatch: () => {
            appendErrorLog(
                '選択したボード種別(オレンジ)と、接続されたボードの種別が一致しません。正しいボード種別を選び直してください。',
            );
            showToastAsync(
                '選択したボード種別と実際のボードが一致しません。もう一度選び直してください。',
                'error',
                5000,
            );
        },
    });

    // Web Serialフォールバック中にブリッジが後から起動したとき、開きっぱなしのポートを解放し
    // hasSelectedBoardをリセットしてブリッジ側の自動検出に切り替える(F5/USB抜き差しと同じ効果を自動で行う)
    const wasWebSerialPathRef = useRef(useWebSerialPath);
    useEffect(() => {
        const wasWebSerialPath = wasWebSerialPathRef.current;
        wasWebSerialPathRef.current = useWebSerialPath;
        if (wasWebSerialPath && !useWebSerialPath) {
            disconnect();
            setHasSelectedBoard(false);
            setPort(null);
        }
    }, [useWebSerialPath, disconnect, setHasSelectedBoard, setPort]);

    // ブリッジ経由のブルーボードに対してchip_idを問い合わせる(Web Serial APIパスはconnect()内の
    // onChipIdResolvedで既に処理済みのためここでは何もしない)。isBoardConnectedはこの問い合わせ結果
    // (boardIdentity)に依存するため、トリガーには使えない(使うと問い合わせが永遠に発火しない
    // デッドロックになる)。
    //
    // 2026-07-23時点、ROM経由(esptool read_mac、ブリッジ側`read_chip_id`メッセージ)に統一済み。
    // ファームウェアの書き込み状態に依存しないため、旧SCMY_ID?方式で必要だった自動リトライは
    // 不要(ROM通信は決定論的で、応答が来ないのはポート自体が使えない等の実質的なエラーのみ)。
    //
    // 「接続済みなのにまだ結果が確定していない(chipIdQueryStatus==='idle')」を条件にすることで、
    // このeffectが再評価されるたび(マウント直後を含む)取りこぼしなく試行できる。一度
    // succeeded/timed_outになれば'idle'に戻るまで再発火しないため、無限リトライにはならない。
    // true→falseエッジ(切断)では、別ボードへの繋ぎ直しで古い情報が残らないようキャッシュをクリアする。
    //
    // ROM通信は実機のリセット・書き込みモード突入を伴うため、シリアルモニターが同じポートを
    // ストリーミング中だと干渉する。そのため問い合わせ前に自発的にモニターを閉じる。
    // ブリッジ経由のchip_id解決は、ここ(親ツールバー)では一切行わない。2026-07-23に
    // 「ボード選択は常にモーダル(BoardSelectorAuto.tsx)経由のみ」という方針にした経緯があり、
    // モーダルで実際に選んだボードについてのみ解決する(モーダル側のhandlePickDetectedが
    // 解決済みのchip_id/boardIdentityをストアへ引き継ぐ)。ここで自動的に問い合わせてしまうと、
    // モーダルとツールバーの両方が同時に同じポートへ問い合わせて競合する(実機で確認済み、
    // ポートの奪い合いによるchip_id取得失敗の主要因になっていた)。
    // このeffectの役目は、ボードが検出できなくなった(差し替え含む)場合に、モーダルでの
    // 選び直しを必須にするため常に未確定の状態へ戻すことだけ。
    const wasPhysicallyConnectedRef = useRef(isPhysicallyConnected);
    const lastMatchedSerialNumberRef = useRef(matchedBoardSerialNumber);
    // 下の自動chip_id問い合わせeffectが「このport+serial_numberには問い合わせ済みか」を
    // 判定するためのref。ここ(切断/入れ替え検知)でも一緒にクリアする必要がある。そうしないと、
    // 同じ実機が切断→再接続した場合に、chipId/boardIdentityは上でnullへ戻したのに
    // 「既に問い合わせ済み」のrefだけ残ってしまい、再問い合わせされないまま情報が消えた
    // 状態で固まってしまう。
    const queriedBoardKeyRef = useRef<string | null>(null);
    useEffect(() => {
        const wasConnected = wasPhysicallyConnectedRef.current;
        wasPhysicallyConnectedRef.current = isPhysicallyConnected;
        const previousSerialNumber = lastMatchedSerialNumberRef.current;
        lastMatchedSerialNumberRef.current = matchedBoardSerialNumber;
        // 両方のシリアル番号が判明していて、かつ異なる場合のみ「実機が入れ替わった」とみなす
        // (どちらか未取得の間は誤検知を避けるため通常の切断判定に任せる)。
        const identityChanged =
            !!previousSerialNumber && !!matchedBoardSerialNumber && previousSerialNumber !== matchedBoardSerialNumber;

        if ((wasConnected && !isPhysicallyConnected) || identityChanged) {
            setChipId(null);
            setBoardIdentity(null);
            setChipIdQueryStatus('idle');
            queriedBoardKeyRef.current = null;
        }
    }, [isPhysicallyConnected, matchedBoardSerialNumber, setChipId, setBoardIdentity, setChipIdQueryStatus]);

    // 検出されているUSBボードが1台だけの場合に限り、親ツールバー自身がchip_id解決を行う
    // (2台以上検出されている間はどれを対象にするか一意に決まらないため、モーダルでの選択に
    // 委ねる)。
    // 2026-07-23時点では「モーダルとツールバーが同時に同じポートへ問い合わせて衝突する」実機の
    // 不具合があったため、この自動問い合わせを撤廃しモーダル専用にしていた(上のeffectのコメント
    // 参照)。2026-07-29、ブリッジ側にper-portロックを導入し同じポートへの同時アクセスが
    // 直列化されるようになったため、実機確認の上でユーザー了承を得て復活させた。
    //
    // 再発火の判定は、chipIdQueryStatus(状態遷移)ではなく「このport+serial_numberに対して
    // 既に問い合わせを開始したか」を専用のref(queriedBoardKeyRef、宣言は上のeffect参照)で
    // 管理する。状態遷移ベースだと、port更新(別effect経由)とchipIdQueryStatusのリセット
    // (idleへ戻る)が別レンダーで起きた場合、このeffect自身の依存配列にchipIdQueryStatusを
    // 含めない限りその遷移を取りこぼして二度と再発火しない不具合が実機で発生した(2台接続中の
    // 1台を外して1台だけ残るケース)。refベースなら、対象の実機(port+serial_number)が
    // 変わった時にだけ自然に再問い合わせされる。
    useEffect(() => {
        if (useWebSerialPath) return; // Web Serial APIはconnect()内のonChipIdResolvedで別途解決済み
        if (!bridgeSupportsChipId) return; // 旧ブリッジ(read_chip_id未対応)には無応答になるだけの問い合わせを送らない
        if (board !== 'blue') return; // オレンジはchip_id解決が不要
        if (!isPhysicallyConnected || !port) return;
        if (detectedBoards.filter((b) => !b.is_bluetooth).length !== 1) return;

        const boardKey = `${port}:${matchedBoardSerialNumber ?? ''}`;
        if (queriedBoardKeyRef.current === boardKey) return;
        queriedBoardKeyRef.current = boardKey;

        // BoardSelectorAuto(モーダル)が選択時点で既にchip_id取得に成功し、ストアへ反映済みの
        // ケースがある。ここで確認せず無条件にpendingへ戻して再問い合わせすると、モーダルの
        // 成功結果を一瞬打ち消して「検出中」の表示が挟まってしまう(実機で確認済み)。
        // succeededの場合のみスキップする。timed_outはここに含めない: 失敗時は下の
        // .then()内でqueriedBoardKeyRef.current = nullに戻し、次の再評価で自動リトライ
        // できるようにする既存の設計があるため、ここでtimed_outもスキップ対象にすると、
        // (モーダル由来か親自身の失敗かを区別できず)そのリトライ機構を壊してしまう。
        // なお上のコメントの通り、chipIdQueryStatus自体は依存配列には含めない
        // (含めると別の不具合が実機で再発したため、値の参照のみに留める)。
        if (chipIdQueryStatus === 'succeeded') return;

        let cancelled = false;
        setChipIdQueryStatus('pending');
        queryChipIdOverBridge(port).then((id) => {
            if (cancelled) return;
            setChipId(id);
            if (id !== null) {
                setChipIdQueryStatus('succeeded');
                fetchBoardIdentity(id);
            } else {
                setChipIdQueryStatus('timed_out');
                // 失敗時はこのキーの問い合わせ済み記録を消し、同じ実機のままでも次にこの
                // effectが再評価されたタイミングで再試行できるようにする。
                queriedBoardKeyRef.current = null;
            }
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        useWebSerialPath,
        bridgeSupportsChipId,
        board,
        isPhysicallyConnected,
        port,
        matchedBoardSerialNumber,
        detectedBoards,
        setChipId,
        setChipIdQueryStatus,
        fetchBoardIdentity,
    ]);

    const { sendSerialMonitorStatus } = useBoardBroadcast({
        board,
        osType,
        port,
        baud,
        hasSelectedBoard,
        isWebSocketConnected,
        isBridgeConnected,
        isBoardConnected,
        isDark,
        boardIdentity,
        chipIdQueryStatus,
        monitorXMode,
        monitorTimeWindow,
        monitorYMode,
        monitorYMin,
        monitorYMax,
        isUploading,
        detectedBoards,
        setIsMonitorOpen,
        writeToSerial: writeToSerial,
        setMonitorXMode,
        setMonitorTimeWindow,
        setMonitorYMode,
        setMonitorYMin,
        setMonitorYMax,
    });

    useEffect(() => {
        sendSerialMonitorStatusRef.current = sendSerialMonitorStatus;
    }, [sendSerialMonitorStatus]);

    const handleMonitor = useCallback(() => {
        const hasBluetoothBoard = detectedBoards.some((board) => board.is_bluetooth);

        if ((!isBoardConnected || !port) && !hasBluetoothBoard) {
            appendLog('⚠️スクーミーボードが接続されていません');
            return;
        }
        if (monitorWindowRef.current && !monitorWindowRef.current.closed) {
            monitorWindowRef.current.focus();
            return;
        }
        // monitorWindowRef(このタブのuseRef)は親タブのページリロードで失われるが、isMonitorOpen
        // はモニター窓自身にBroadcastChannel経由で生存確認しているため(useBoardBroadcast.ts参照)、
        // リロード後も正しい値になり得る。refが無いのにisMonitorOpenがtrueの場合、window.open()を
        // 再度呼ぶと(既存窓と同じURLへの再ナビゲーションになり)中身がリロードされてログが消えたり
        // 二重に開いてしまう不具合が実際に確認されたため、window.open()自体は使わない。代わりに
        // モニター窓自身へBroadcastChannel経由で'focus_monitor'を送り、window.focus()を自分自身に
        // 呼んでもらう(自窓へのfocus()は常に許可されるため、Windowオブジェクトの参照が無くても叶う)。
        if (isMonitorOpen) {
            appendLog('⚠️シリアルモニターは既に開いています。既存のウィンドウを確認してください。');
            const bc = new BroadcastChannel('schoomy-board');
            bc.postMessage({ type: 'focus_monitor' });
            bc.close();
            return;
        }
        const portText = useWebSerialPath ? '' : `${port} `;
        appendLog(`🖥️シリアルモニターを起動しました。 ${portText}@${baud}bps`);
        monitorWindowRef.current = window.open(
            '/boards/monitor',
            'SerialMonitor',
            'width=1000,height=700,resizable=yes',
        );
    }, [isBoardConnected, port, baud, appendLog, useWebSerialPath, detectedBoards, isMonitorOpen]);

    const { handleUpload } = useBoardUpload({
        board,
        code,
        port,
        blockXml: currentBlockXml,
        isBoardConnected,
        isUploading,
        wsRef,
        useWebSerial: useWebSerialPath,
        onUploadStart: () => {
            resetUpload();
            setActiveUploadRequestId(null);
            setIsUploading(true);
        },
        setActiveRequestId: (id) => setActiveUploadRequestId(id),
        onUploadEvent: (e) => applyUploadEvent(e),
        onWebSerialApiUpload: webSerialApiUpload,
        onLog: appendLog,
        onError: handleError,
    });

    const handleBoardPicked = useCallback(
        (picked: BoardType | null) => {
            if (!picked) return;
            setBoard(picked);
            setHasSelectedBoard(true);
            // Web Serial APIパスはこの時点ではまだポート選択・実機検証前なので、ここではログを出さない。
            // 実際に接続確認できたタイミング(useBoardWebSerialApiのconnect()成功時)でログを出す。
            if (!useWebSerialPath) {
                appendLog(`${BOARD_CONFIG[picked].label}に接続しました。`);
            }
            requestBoardList();
            if (!useBoardEditorFileStore.getState().currentFileId) {
                setCode(getInitialCode(picked));
            }
        },
        [setBoard, setHasSelectedBoard, appendLog, requestBoardList, setCode, useWebSerialPath],
    );

    const handleOpenBoardSelector = useCallback(() => {
        // モーダル選択は board に依存させない(未選択時に board が古い値を持っている可能性があるため)。
        // OS + ブリッジ接続状態のみで、ブリッジ方式/Web Serial方式のどちらの選択UIを出すか決める。
        const useWebSerialSelector = isChromebook || !isBridgeConnected;
        const SelectedComponent = useWebSerialSelector ? (
            <BoardSelectorAutoWebSerialApi onChange={handleBoardPicked} onConnect={connect} />
        ) : (
            <BoardSelectorAuto onChange={handleBoardPicked} />
        );
        const modalSize = !useWebSerialSelector
            ? { pcWidth: '700px', mbWidth: '90%', pcHeight: '460px', mbHeight: '80%' }
            : isChromebook
            ? { pcWidth: '700px', mbWidth: '90%', pcHeight: '280px', mbHeight: '80%' }
            : { pcWidth: '700px', mbWidth: '90%', pcHeight: '360px', mbHeight: '80%' };
        openModal({
            id: 'board-selector-modal',
            component: SelectedComponent,
            size: modalSize,
            direction: 'clip',
            overFlowType: 'auto',
        });
    }, [openModal, handleBoardPicked, connect, isChromebook, isBridgeConnected]);

    const displayUI = useMemo(() => {
        // boardはlocalStorageから同期的に初期化される(useBoardEditorStore.tsのgetInitialBoard)ため、
        // SSR(localStorage無し→常にorange)とクライアント初回描画(localStorageの実値)でズレて
        // hydrationエラーになる。mounted完了までは常にorange扱いにして、この不一致を避ける。
        const effectiveBoard = mounted ? board : 'orange';
        if (hasSelectedBoard) {
            const boardUI = BOARD_UI[effectiveBoard];
            const boardName = effectiveBoard === 'blue' ? boardIdentity?.bluetooth_device_name ?? undefined : undefined;
            const isFullyConnected = isBoardConnected && !!port && !port.includes('検出中');
            return {
                label: boardUI.label,
                labelColor: 'text-color-base',
                bgColor: isFullyConnected ? boardUI.bgColor : DISCONNECTED_BG_COLOR,
                // ポートは物理的な接続の事実(isPhysicallyConnected)だけで出す。「利用不可」確定
                // (未所属/未対応hub種別)でも、実際にどのポートに挿さっているかは有用な情報のため。
                port: isPhysicallyConnected ? port ?? '検出中...' : '－',
                boardImageSrc: BOARD[effectiveBoard].icon.src,
                boardName,
                isSelected: true,
            };
        }
        // 未選択時はブロックの色設定(effectiveBoard)に関係なく常にオレンジ画像を使い、
        // グレースケール化してニュートラルさを出す(まだ何色のボードが繋がるか分からないため)
        return {
            label: '使用するスクーミーボードを選択してください',
            labelColor: 'text-color-co-gray-dark',
            bgColor: DISCONNECTED_BG_COLOR,
            port: '－',
            boardImageSrc: BOARD.orange.icon.src,
            isSelected: false,
        };
    }, [
        mounted,
        hasSelectedBoard,
        board,
        isBoardConnected,
        isPhysicallyConnected,
        port,
        isWebSocketConnected,
        isBridgeConnected,
        useWebSerialPath,
        boardIdentity,
    ]);

    const statusText = useMemo(() => {
        if (!useWebSerialPath) {
            if (!hasSelectedBoard || !isWebSocketConnected || !isBridgeConnected) return undefined;
            // 確認は完了しており、その結果として使用不可と確定した状態。まだ結果待ちの「検出中」
            // とは意味が異なる(一時的ではなく確定した状態)ため、別文言で明示する。
            if (isConfirmedUnusableBlueBoard) return '利用不可';
            if (!isBoardConnected) return '検出中';
            // ROM経由(esptool read_mac)でchip_idを問い合わせている間。Web Serial API側の
            // isChipIdProbingと同じ考え方で、確認中であることを明示しないと接続中に見えてしまう。
            if (chipIdQueryStatus === 'pending') return '確認中...';
            if (!port || port.includes('検出中')) return '検出中';
        } else {
            if (!hasSelectedBoard) return undefined;
            // ネイティブピッカーでポートを選んだ直後、実機がROM経由で本当に選んだボード種別と
            // 一致するか確認している間(オレンジ選択時のミスマッチ判定/ブルー選択時のchip_id取得)。
            // これが無いと、この確認中(数百ms〜、ブルーの取りこぼしリトライ時は最大1分弱)は画面が
            // 固まったように見えてしまう。
            if (isChipIdProbing) return '確認中...';
            if (isConfirmedUnusableBlueBoard) return '利用不可';
            if (!isBoardConnected) return '未接続';
            if (!port || port.includes('検出中')) return '未接続';
        }
        return '接続中';
    }, [
        hasSelectedBoard,
        isWebSocketConnected,
        isBridgeConnected,
        isBoardConnected,
        isConfirmedUnusableBlueBoard,
        port,
        useWebSerialPath,
        isChipIdProbing,
        chipIdQueryStatus,
    ]);

    const toolbarBgClass = isDark ? 'bg-color-ide-frame-dark-bg' : 'bg-color-ide-frame-bg';
    const hasBluetoothBoard = detectedBoards.some((board) => board.is_bluetooth);

    return (
        <div
            className={`w-full mb-0 flex gap-0 flex-row items-center justify-between ${toolbarBgClass} rounded-t-md px-3 py-1`}
        >
            <div className="flex items-center gap-2">
                <div
                    className="relative flex justify-center items-center rounded-full bg-color-white-base w-auto h-[52px] shadow-right-bottom-mid cursor-pointer px-2"
                    onClick={handleOpenBoardSelector}
                    aria-busy={!isFontReady}
                    aria-live="polite"
                >
                    {!isFontReady && (
                        <div className="absolute inset-0 flex items-center justify-center rounded-full bg-white/90">
                            <ImageAdaptive src="/images/common/loading-progress.svg" baseWidth={18} />
                        </div>
                    )}
                    <BoardStatusIcon
                        hidden={!isFontReady}
                        boardImageSrc={displayUI.boardImageSrc}
                        isSelected={displayUI.isSelected}
                    />
                    <BoardStatusLabel
                        label={displayUI.label}
                        labelColor={displayUI.labelColor}
                        statusText={statusText}
                        hidden={!isFontReady}
                    />
                    {/* レスポンシブ対応: ButtonGAdaptiveのラベルが隠れるのと同じタイミング(isUploadLabelVisible)で隠す */}
                    {isUploadLabelVisible && (
                        <BoardExtraInfo
                            port={displayUI.port}
                            board={board}
                            isBoardConnected={isBoardConnected}
                            isPhysicallyConnected={isPhysicallyConnected}
                            useWebSerialPath={useWebSerialPath}
                            boardIdentity={boardIdentity}
                            hidden={!isFontReady}
                            visible={mounted && isBridgeStatusResolved}
                        />
                    )}
                    {/* 旧ブリッジ(bridgeSupportsChipId=false)はhub所属自体を判定しない(後方互換)ため、
                        「unknown」バッジも一切出さない。判定できないことと使用不可は別物なので混同しない */}
                    {hasSelectedBoard && board === 'blue' && bridgeSupportsChipId && (
                        <div className="flex justify-center items-center pl-2 pr-1" aria-hidden={!isFontReady}>
                            <HubAffiliationIcon
                                isBoardConnected={isBoardConnected}
                                affiliation={hubTypeCdToAffiliation(boardIdentity?.hub_type_cd)}
                                isLoading={useWebSerialPath ? isChipIdProbing : chipIdQueryStatus === 'pending'}
                            />
                        </div>
                    )}
                </div>
                <ButtonGAdaptive
                    icon={Monitor}
                    size="md"
                    onClick={handleMonitor}
                    disabled={(!isBoardConnected || !port) && !hasBluetoothBoard}
                />
            </div>
            <div className="flex items-center gap-2">
                <ButtonGAdaptive
                    icon={useWebSerialPath ? MonitorUp : '/images/block/pc_bridge.png'}
                    label={
                        isUploadLabelVisible
                            ? useWebSerialPath
                                ? 'ブラウザ経由でボードへ書き込み'
                                : 'ブリッジ経由でボードへ書き込み'
                            : ''
                    }
                    description={
                        isUploadLabelVisible
                            ? useWebSerialPath
                                ? 'upload to SchooMy board via browser'
                                : 'upload to SchooMy board via bridge'
                            : ''
                    }
                    size="md"
                    onClick={handleUpload}
                    disabled={!isBoardConnected || !port || !currentFileId}
                    isProcessing={isUploading}
                    lockIconSizeToText
                />
            </div>
        </div>
    );
}

function BoardStatusIcon({
    hidden,
    boardImageSrc,
    isSelected,
}: {
    hidden: boolean;
    boardImageSrc: string | null;
    isSelected: boolean;
}) {
    // ボード種別切り替え直後の再描画と競合して画像リクエストが中断され、空欄のまま戻らなく
    // なることがある(Blocklyのブロックアイコンで実際に確認・修正済みの現象と同根)。読み込み
    // 失敗時に、同じsrcへクエリを付けて1回だけ再取得を試みる。
    const [retrySrc, setRetrySrc] = useState<string | null>(null);
    const hasRetriedRef = useRef(false);

    useEffect(() => {
        hasRetriedRef.current = false;
        setRetrySrc(null);
    }, [boardImageSrc]);

    const handleError = useCallback(() => {
        if (hasRetriedRef.current || !boardImageSrc) return;
        hasRetriedRef.current = true;
        setTimeout(() => {
            setRetrySrc(`${boardImageSrc}${boardImageSrc.includes('?') ? '&' : '?'}_retry=${Date.now()}`);
        }, 100);
    }, [boardImageSrc]);

    return (
        <div className="flex justify-center items-center pl-0 pr-1" aria-hidden={hidden}>
            {boardImageSrc && (
                <img
                    key={boardImageSrc}
                    src={retrySrc ?? boardImageSrc}
                    alt=""
                    onError={handleError}
                    className={isSelected ? undefined : 'grayscale opacity-60'}
                    style={{ width: 46, height: 46, objectFit: 'contain', flexShrink: 0 }}
                />
            )}
        </div>
    );
}

function BoardStatusLabel({
    label,
    labelColor,
    statusText,
    hidden,
}: {
    label: string;
    labelColor: string;
    boardName?: string;
    statusText?: string;
    hidden: boolean;
}) {
    const isDetecting = statusText?.includes('検出中');
    const isConnected = statusText === '接続中';
    const isProbing = statusText === '確認中...';
    const dotClass = isConnected ? 'bg-color-ide-board-connected' : 'bg-gray-300';
    const animateProps = isConnected ? { opacity: [1, 0.3, 1] } : { opacity: 1 };
    const transitionProps: Transition = isConnected ? { duration: 2, repeat: Infinity } : { duration: 0 };
    return (
        <div className="flex flex-col justify-center pl-1 pr-2" aria-hidden={hidden}>
            <div className={`font-corp font-medium text-sm leading-tight whitespace-nowrap ${labelColor}`}>{label}</div>
            {statusText && (
                <div className="flex items-center gap-1 mt-0.5 whitespace-nowrap">
                    {/* ドット⇔スピナーの切り替えで右の文字がズレないよう、幅を固定する
                        (BoardSelectorAuto.tsxのモーダル行と同じ考え方)。ドットは真ん中より
                        ほんの少し上に見えるよう1pxだけ引き上げる */}
                    <div className="flex items-center justify-center" style={{ width: 13, height: 13 }}>
                        {isProbing ? (
                            <ImageAdaptive
                                src="/images/common/loading-circle.svg"
                                baseWidth={13}
                                breakpoints={{ md: 13 }}
                            />
                        ) : (
                            <motion.div
                                className={`w-1.5 h-1.5 rounded-full ${dotClass}`}
                                style={{ marginTop: -1 }}
                                animate={animateProps}
                                transition={transitionProps}
                            />
                        )}
                    </div>
                    <span
                        className={`font-cantarell font-medium text-[11px] leading-tight whitespace-nowrap ${
                            isConnected ? 'text-color-ide-board-connected' : 'text-gray-400'
                        }`}
                    >
                        {isDetecting ? '検出中...' : statusText}
                    </span>
                </div>
            )}
        </div>
    );
}

function HubAffiliationIcon({
    isBoardConnected,
    affiliation,
    isLoading,
}: {
    isBoardConnected: boolean;
    affiliation: keyof typeof HUB_AFFILIATION | null;
    // chip_id取得中(自動リトライ込み、Web Serial APIのみ)。まだ結果が確定していないので
    // 「unknown」を早まって出さず、ローディング表示にする。
    isLoading?: boolean;
}) {
    // ローディング表示はBoardStatusLabel側の「確認中...」スピナーで既に出しているため、
    // ここでは何も表示しない(以前は36pxのスピナーを重ねて出しており二重表示になっていた)。
    if (isLoading) {
        return null;
    }
    // 未接続、または使用不可(未所属・未対応hub種別)の場合は同じ中立表示にする(グレー背景・枠線無し)。
    // isBoardConnectedは使用可能な3種のいずれかである場合のみtrueになるよう既に算出されているため、
    // ここに来る時点でaffiliationはnullではないはずだが、念のため両方をガードする。
    if (!isBoardConnected || !affiliation) {
        return (
            <div
                className="flex items-center gap-2 rounded-full pl-3 pr-4 py-1.5"
                style={{ backgroundColor: '#9CA3AF' }}
            >
                <IconAdaptive
                    Icon={CircleSlash}
                    strokeWidth={2.0}
                    baseSize={18}
                    breakpoints={{ md: 18 }}
                    className="text-white"
                />
                <span className="font-montserrat font-bold text-white text-sm whitespace-nowrap">unknown</span>
            </div>
        );
    }

    const hub = HUB_AFFILIATION[affiliation];
    return (
        <div className="flex items-center gap-2 rounded-full pl-3 pr-4 py-1.5" style={{ backgroundColor: hub.color }}>
            <IconAdaptive
                Icon={hub.Icon}
                strokeWidth={2.0}
                baseSize={18}
                breakpoints={{ md: 18 }}
                className="text-white"
            />
            <span className="font-montserrat font-bold text-white text-sm whitespace-nowrap">{hub.label}</span>
        </div>
    );
}

// トールバーは横幅に余裕があるので1行にまとめる
function BoardExtraInfo({
    port,
    board,
    isBoardConnected,
    isPhysicallyConnected,
    useWebSerialPath,
    boardIdentity,
    hidden,
    visible,
}: {
    port: string;
    board: BoardType;
    isBoardConnected: boolean;
    isPhysicallyConnected: boolean;
    useWebSerialPath: boolean;
    boardIdentity: BoardIdentifyResponse | null;
    hidden: boolean;
    visible: boolean;
}) {
    // 「検出中...」等のプレースホルダーはデータが無い状態なのでトルツメ対象に含める。
    // Web Serial APIはブラウザ仕様上、実際のポート名を取得できないためPort自体を表示しない。
    // Portは物理的な接続の事実だけで出す(isBoardConnectedではなくisPhysicallyConnectedを使う)。
    // 「利用不可」確定(未所属/未対応hub種別)でも、どのポートに挿さっているかは有用な情報のため。
    const hasPort = !useWebSerialPath && isPhysicallyConnected && !!port && !port.includes('検出中') && port !== '－';
    // chip_id問い合わせがまだ完了していない/失敗した場合はboardIdentityがnullのままなので、
    // その間はトルツメ(モック値は出さない)。hub所属が未所属/未対応(unknown)の間もcd/BT名は
    // 出さずポートだけにする(BT名はcdが無くても決定論的に計算できてしまうため、hub所属の
    // 判定と無関係に出てしまわないよう明示的にゲートする)。
    const hasBoardName = board === 'blue' && isBoardConnected && isUsableHubType(boardIdentity?.hub_type_cd) && boardIdentity?.cd != null;
    const hasBluetoothName =
        board === 'blue' &&
        isBoardConnected &&
        isUsableHubType(boardIdentity?.hub_type_cd) &&
        !!boardIdentity?.bluetooth_device_name;
    // 中身が何も無い(全部トルツメ)場合にpaddingだけ復活して余白が伸びて見えるのを防ぐため、
    // 表示する中身が実際にある時だけ幅・paddingを持たせる
    const hasContent = hasPort || hasBoardName || hasBluetoothName;
    const showPadding = visible && hasContent;
    return (
        <div
            className="flex items-center px-2 whitespace-nowrap"
            aria-hidden={hidden || !showPadding}
            style={{
                visibility: showPadding ? 'visible' : 'hidden',
                width: showPadding ? undefined : 0,
                padding: showPadding ? undefined : 0,
                overflow: 'hidden',
            }}
            suppressHydrationWarning
        >
            {/* 切断時・未解決時はトルツメ(ダッシュ表示ではなく要素ごと非表示) */}
            {hasBoardName && (
                <>
                    <div className="flex justify-center items-center w-[22px] h-[22px]">
                        <IconAdaptive
                            Icon={Cpu}
                            strokeWidth={2.0}
                            baseSize={17}
                            breakpoints={{ md: 17 }}
                            className="text-color-base"
                        />
                    </div>
                    <span className="font-cantarell font-medium text-color-co-gray-dark text-sm pr-2">
                        {boardIdentity?.cd != null ? String(boardIdentity.cd).padStart(5, '0') : ''}
                    </span>
                </>
            )}
            {hasBluetoothName && (
                <>
                    <div className="flex justify-center items-center w-[22px] h-[22px]">
                        <IconAdaptive
                            Icon={Bluetooth}
                            strokeWidth={2.0}
                            baseSize={17}
                            breakpoints={{ md: 17 }}
                            className="text-color-base"
                        />
                    </div>
                    <span className="font-cantarell font-medium text-color-co-gray-dark text-sm pr-2">
                        {boardIdentity?.bluetooth_device_name}
                    </span>
                </>
            )}

            {hasPort && (
                <>
                    <div className="flex justify-center items-center w-[22px] h-[22px]">
                        <IconAdaptive
                            Icon={Usb}
                            strokeWidth={2.0}
                            baseSize={17}
                            breakpoints={{ md: 17 }}
                            className="text-color-base"
                        />
                    </div>
                    <span className="font-cantarell font-medium text-color-co-gray-dark text-sm">{port}</span>
                </>
            )}
        </div>
    );
}
